import { MCP_URL } from "./urls.js";
import { resolveCliToken } from "./status.js";
import { getAccessToken } from "./auth.js";
import { withNextStep } from "./guidance.js";
import { CARRIER_VERSION } from "../../version.js";

export interface AskResult {
  ok: boolean;
  text: string;
  /**
   * `structuredContent` from the tool result, when it sent any.
   *
   * UI-enabled tools return their data here rather than only as prose, which is
   * what lets `carrier dash` render the very same screen object the MCP panel
   * renders instead of re-deriving it from formatted text.
   */
  structured?: unknown;
}

export interface McpCallOptions {
  /** Recovery steps appended when the tool itself reports an error. */
  errorHints?: string[];
}

const DEFAULT_ERROR_HINTS = [
  "Re-check the arguments: carrier <domain> <command> --help",
  "Or open Claude and ask there with full tool routing",
];

// ---------------------------------------------------------------------------
// Client-side deadline (issue #663)
//
// Without this the CLI inherits fetch's absence of a default timeout, so a
// wedged tool (get_tariff, ~2-3 MB) hangs the terminal with zero output —
// killed manually at 200s in the issue report.
//
// 45s, chosen from measured latency:
//   - discrete tool calls return in 0.5-1.5s;
//   - the largest known-good MCP result (233 KB) took 0.75s;
//   - `carrier intelligence fleet-health`, the slowest legitimate command,
//     fans out over several OCS calls and measured 1.47s / 2.84s / 1.47s here;
//     `marketing` 2.70s, `coverage` 1.98s.
// The slowest real command is under 3s, so 45s is ~15x headroom.
//
// It is deliberately LONGER than the server's 20s OCS deadline: the server may
// legitimately make several sequential OCS calls per tool (intelligence
// composites), plus handshake and transport. Giving the outer layer a shorter
// budget would cut off work the inner layer still considers healthy, and would
// hide the server's specific, actionable OCS timeout message behind a generic
// client one. Inner deadline fires first for a single wedged upstream; the
// outer one is the backstop for anything the server itself cannot bound.
// ---------------------------------------------------------------------------

const DEFAULT_MCP_TIMEOUT_MS = 45_000;

export function mcpTimeoutMs(): number {
  const parsed = Number(process.env.CARRIER_MCP_TIMEOUT_MS);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_MCP_TIMEOUT_MS;
}

/** Shared deadline for the whole initialize → tools/call handshake. */
export class McpTimeoutError extends Error {
  constructor(public readonly timeoutMs: number) {
    super(`timed out after ${Math.round(timeoutMs / 1000)}s`);
    this.name = "McpTimeoutError";
  }
}

function isTimeoutish(err: unknown): boolean {
  return (
    err instanceof McpTimeoutError ||
    (err instanceof Error &&
      (err.name === "AbortError" || err.name === "TimeoutError"))
  );
}

function parseRpcBody(raw: string): {
  result?: { content?: Array<{ type?: string; text?: string }>; isError?: boolean; structuredContent?: unknown };
  error?: { message?: string };
} {
  const trimmed = raw.trim();
  if (!trimmed) return {};
  // Streamable HTTP MCP can return SSE (`event: message\ndata: {...}`)
  // or a bare JSON-RPC object. Accept both.
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    return JSON.parse(trimmed) as {
      result?: { content?: Array<{ type?: string; text?: string }>; isError?: boolean; structuredContent?: unknown };
      error?: { message?: string };
    };
  }
  const dataLine = trimmed
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.startsWith("data:"));
  if (!dataLine) return {};
  return JSON.parse(dataLine.slice(5).trim()) as {
    result?: { content?: Array<{ type?: string; text?: string }>; isError?: boolean; structuredContent?: unknown };
    error?: { message?: string };
  };
}

/**
 * Call one tool on the remote MCP when a headless token is present.
 *
 * Streamable HTTP MCP requires initialize → Mcp-Session-Id → tools/call.
 * A bare tools/call returns 400 "Mcp-Session-Id header is required", so every
 * CLI command routes through here instead of posting tools/call on its own.
 */
export async function callMcpTool(
  tool: string,
  args: Record<string, unknown>,
  opts: McpCallOptions = {},
): Promise<AskResult> {
  // An env credential resolves synchronously and MUST stay that way: awaiting
  // here would defer creation of the deadline timer below past the caller's
  // first tick, which is exactly what the #663 timeout tests drive. Only the
  // stored-login path — which has to touch disk and maybe refresh — awaits.
  const token = resolveCliToken() ?? (await getAccessToken());
  if (!token) {
    return {
      ok: false,
      text: withNextStep("Not signed in.", [
        "Sign in from this terminal: carrier login",
        "No account yet? carrier open signup",
        "Headless/CI instead: export CARRIER_API_KEY=ak_… from Console → Settings → API Keys",
      ]),
    };
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Accept: "application/json, text/event-stream",
  };

  // #663 — one deadline covers the whole handshake (initialize → notify →
  // tools/call → body read). Aborting the controller tears down whichever
  // request is in flight; the race guarantees we return even if it does not.
  const timeoutMs = mcpTimeoutMs();
  const controller = new AbortController();
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_resolve, reject) => {
    deadlineTimer = setTimeout(() => {
      controller.abort();
      reject(new McpTimeoutError(timeoutMs));
    }, timeoutMs);
  });
  const signal = controller.signal;

  try {
    return await Promise.race([
      (async (): Promise<AskResult> => {
        const initRes = await fetch(MCP_URL, {
          method: "POST",
          headers,
          signal,
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "initialize",
            params: {
              protocolVersion: "2024-11-05",
              capabilities: {},
              clientInfo: { name: "carrier-cli", version: CARRIER_VERSION },
            },
          }),
        });

        if (initRes.status === 401 || initRes.status === 403) {
          return {
            ok: false,
            text: withNextStep(`MCP returned ${initRes.status} (auth rejected).`, [
              "Confirm CARRIER_API_KEY is a valid org key (ak_…) from app.carrier.llc",
              "Or complete OCS onboarding: https://app.carrier.llc/onboarding",
              "Interactive: use Claude + OAuth instead of a headless key",
            ]),
          };
        }

        const sessionId =
          initRes.headers.get("mcp-session-id") ?? initRes.headers.get("Mcp-Session-Id");
        await initRes.arrayBuffer().catch(() => undefined);
        if (!sessionId) {
          return {
            ok: false,
            text: withNextStep("MCP initialize did not return a session id.", [
              "Check network access to https://mcp.carrier.llc/mcp",
              "Retry later, or use Claude with the registered MCP (OAuth path)",
            ]),
          };
        }

        const sessionHeaders = { ...headers, "Mcp-Session-Id": sessionId };
        await fetch(MCP_URL, {
          method: "POST",
          headers: sessionHeaders,
          signal,
          body: JSON.stringify({ jsonrpc: "2.0", method: "notifications/initialized" }),
        }).catch(() => undefined);

        const res = await fetch(MCP_URL, {
          method: "POST",
          headers: sessionHeaders,
          signal,
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: 2,
            method: "tools/call",
            params: { name: tool, arguments: args },
          }),
        });

        if (!res.ok) {
          const snippet = (await res.text().catch(() => "")).slice(0, 240);
          return {
            ok: false,
            text: withNextStep(`MCP call failed (${res.status}). ${snippet}`, [
              "Check network access to https://mcp.carrier.llc/mcp",
              "Retry later, or use Claude with the registered MCP (OAuth path)",
            ]),
          };
        }

        const json = parseRpcBody(await res.text());

        if (json.error?.message) {
          return {
            ok: false,
            text: withNextStep(json.error.message, opts.errorHints ?? DEFAULT_ERROR_HINTS),
          };
        }

        const parts = json.result?.content ?? [];
        const text =
          parts
            .map((p) => p.text)
            .filter(Boolean)
            .join("\n")
            .trim() || JSON.stringify(json.result ?? json, null, 2);

        return {
          ok: !json.result?.isError,
          text,
          structured: json.result?.structuredContent,
        };
      })(),
      deadline,
    ]);
  } catch (e: unknown) {
    if (isTimeoutish(e)) {
      const secs = Math.round(timeoutMs / 1000);
      return {
        ok: false,
        text: withNextStep(
          `MCP call to ${tool} timed out after ${secs}s (no response).`,
          [
            "The tool is likely returning more data than the MCP transport can deliver — see issue #663",
            "Narrow the request (a specific --account-id / --iccid returns far less than a whole-fleet call)",
            `Raise the deadline for one slow call: CARRIER_MCP_TIMEOUT_MS=${timeoutMs * 2} carrier …`,
            "Confirm the service is up: check network access to https://mcp.carrier.llc/mcp",
            "Or open Claude and ask there with full tool routing",
          ],
        ),
      };
    }
    const msg = e instanceof Error ? e.message : String(e);
    return {
      ok: false,
      text: withNextStep(`Could not reach MCP: ${msg}`, [
        "Verify outbound HTTPS to mcp.carrier.llc",
        "Use the interactive Claude path while offline from this machine",
      ]),
    };
  } finally {
    if (deadlineTimer !== undefined) clearTimeout(deadlineTimer);
  }
}

const ASK_ERROR_HINTS = [
  "Rephrase the intent more specifically (include ICCID / MSISDN if relevant)",
  "Or use a discrete command: carrier --help lists every domain",
];

/**
 * What `carrier_ask` answers with. It is a router: it names the tool to call and
 * the params to call it with, and deliberately does not execute anything itself
 * ("carrier_ask does not auto-execute — the caller makes the explicit tool
 * call"). In the CLI, we are that caller.
 */
interface AskRouting {
  match?: string;
  resolved_tool?: string;
  resolved_params?: Record<string, unknown>;
  confirm_required?: boolean;
  /** Present instead of resolved_tool when the intent matched more than one tool. */
  candidates?: Array<{ tool?: string; reason?: string }>;
  clarifying_question?: string;
}

/** The routing envelope arrives as JSON in a text part; anything else is prose. */
function parseRouting(text: string): AskRouting | undefined {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{")) return undefined;
  try {
    const parsed = JSON.parse(trimmed) as AskRouting;
    if (!parsed || typeof parsed !== "object") return undefined;
    const routes = typeof parsed.resolved_tool === "string" && parsed.resolved_tool;
    const asksBack = typeof parsed.clarifying_question === "string" || Array.isArray(parsed.candidates);
    return routes || asksBack ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Natural-language fallback for anything the discrete subcommands do not cover.
 * Two hops: carrier_ask resolves the intent to a tool, then we call that tool.
 * Without the second hop the user gets routing JSON instead of their answer.
 */
export async function carrierAsk(intent: string): Promise<AskResult> {
  const routed = await callMcpTool("carrier_ask", { intent }, { errorHints: ASK_ERROR_HINTS });
  if (!routed.ok) return routed;

  const routing = parseRouting(routed.text);
  // Not a routing envelope — carrier_ask answered directly, so pass it through.
  if (!routing) return routed;

  // Nothing resolved — surface the question, not the envelope.
  if (!routing.resolved_tool) {
    const candidates = (routing.candidates ?? [])
      .map((c) => (c.tool ? `Candidate: ${c.tool}${c.reason ? ` — ${c.reason}` : ""}` : undefined))
      .filter((s): s is string => Boolean(s));
    return {
      ok: false,
      text: withNextStep(routing.clarifying_question?.trim() || "Carrier could not tell which tool you meant.", [
        ...candidates,
        ...ASK_ERROR_HINTS,
      ]),
    };
  }

  const tool = routing.resolved_tool;
  const args = routing.resolved_params ?? {};

  if (routing.match && routing.match !== "confirmed") {
    return {
      ok: false,
      text: withNextStep(`Carrier could not confirm what you meant (match: ${routing.match}).`, [
        `Closest tool: ${tool}`,
        ...ASK_ERROR_HINTS,
      ]),
    };
  }

  // Money- and service-affecting tools are never fired from a guessed intent.
  if (routing.confirm_required) {
    return {
      ok: false,
      text: withNextStep(`"${intent}" resolves to ${tool}, which changes live service or billing.`, [
        `Params: ${JSON.stringify(args)}`,
        "Run the matching discrete command so every flag is explicit and reviewable",
        "carrier --help lists every domain",
      ]),
    };
  }

  return callMcpTool(tool, args, { errorHints: ASK_ERROR_HINTS });
}
